/**
 * Winner-stays-on knockout state machine for the Soap Showdown tournament.
 *
 * This is NOT 9 independent pairwise comparisons — it is a single-elimination
 * bracket where the winner of each round carries forward as the "champion" to
 * face the next challenger. An eliminated reaction never reappears in the
 * same session. All 10 ids must be introduced across 9 rounds.
 */

/** Fisher-Yates shuffle. Pure — takes an rng so it's deterministic in tests. */
export function shuffle(items, rng = Math.random) {
  const arr = items.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * @param {Array<number|string>} ids all experiment ids, in any order
 * @param {() => number} rng
 */
export function createTournament(ids, rng = Math.random) {
  if (ids.length < 2) {
    throw new Error('Tournament needs at least 2 experiments');
  }
  const order = shuffle(ids, rng);
  const [champion, challenger, ...queue] = order;
  return {
    champion,
    challenger,
    queue,
    round: 1,
    totalRounds: ids.length - 1,
    log: [],
    finished: false,
    winner: null,
  };
}

/**
 * Apply a visitor's pick for the current round. Returns a new state; never
 * mutates the input. Throws if called on a finished tournament or with an
 * id that isn't one of the two current contenders.
 */
export function applyVote(state, pickedId) {
  if (state.finished) {
    throw new Error('Tournament already finished');
  }
  if (pickedId !== state.champion && pickedId !== state.challenger) {
    throw new Error('Picked id is not one of the current round contenders');
  }

  const loserId = pickedId === state.champion ? state.challenger : state.champion;
  const logEntry = { round: state.round, winnerId: pickedId, loserId };
  const log = [...state.log, logEntry];

  if (state.queue.length === 0) {
    return {
      ...state,
      champion: pickedId,
      challenger: null,
      log,
      finished: true,
      winner: pickedId,
    };
  }

  const [nextChallenger, ...restQueue] = state.queue;
  return {
    ...state,
    champion: pickedId,
    challenger: nextChallenger,
    queue: restQueue,
    round: state.round + 1,
    log,
    finished: false,
    winner: null,
  };
}
